import { useEffect, useRef, forwardRef } from "react";

const CustomScroll = forwardRef(({ children, height = "100%" }, ref) => {
    const contentRef = useRef(null);
    const thumbRef = useRef(null);

    const isDragging = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);
    const scrollTimeout = useRef(null);

    useEffect(() => {
        if (ref) {
            ref.current = contentRef.current;
        }

        const content = contentRef.current;
        const thumb = thumbRef.current;

        if (!content || !thumb) return;

        const fadeOut = () => {
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
            scrollTimeout.current = setTimeout(() => {
                if (thumbRef.current) {
                    thumbRef.current.style.opacity = "0";
                }
            }, 2000);
        };

        const showThumb = () => {
            if (thumbRef.current) {
                thumbRef.current.style.opacity = "1";
                thumbRef.current.style.transition = "opacity 0.3s ease-in-out";
            }
            fadeOut();
        };

        // Initialize styles
        if (thumbRef.current) {
            thumbRef.current.style.opacity = "0";
            thumbRef.current.style.transition = "opacity 0.3s ease-in-out";
        }

        const updateThumb = () => {
            const content = contentRef.current;
            const thumb = thumbRef.current;

            if (!content || !thumb) return;

            const scrollHeight = content.scrollHeight;
            const clientHeight = content.clientHeight;

            // Hide scrollbar if not needed
            if (scrollHeight <= clientHeight) {
                thumb.style.display = "none";
                return;
            }

            thumb.style.display = "block";
            showThumb(); // Show on update/scroll

            const thumbHeight =
                (clientHeight / scrollHeight) * clientHeight;

            const maxScrollTop = scrollHeight - clientHeight;
            const scrollTop = content.scrollTop;

            const thumbTop =
                (scrollTop / maxScrollTop) * (clientHeight - thumbHeight);

            thumb.style.height = `${thumbHeight}px`;
            thumb.style.transform = `translateY(${thumbTop}px)`;
        };

        /* ---------- Mouse events ---------- */
        const handleMouseDown = (e) => {
            isDragging.current = true;
            startY.current = e.clientY;
            startScrollTop.current = content.scrollTop;

            // Keep visible while dragging
            if (thumbRef.current) thumbRef.current.style.opacity = "1";
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            e.preventDefault();
        };

        const handleMouseMove = (e) => {
            if (!isDragging.current) return;

            // Keep visible
            if (thumbRef.current) thumbRef.current.style.opacity = "1";
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

            const deltaY = e.clientY - startY.current;
            const scrollHeight = content.scrollHeight;
            const clientHeight = content.clientHeight;

            const thumbHeight =
                (clientHeight / scrollHeight) * clientHeight;

            const maxScrollTop = scrollHeight - clientHeight;

            const scrollDelta =
                (deltaY / (clientHeight - thumbHeight)) * maxScrollTop;

            content.scrollTop = Math.max(
                0,
                Math.min(maxScrollTop, startScrollTop.current + scrollDelta)
            );
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            fadeOut(); // Resume fade out
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        /* ---------- Touch events ---------- */
        const handleTouchStart = (e) => {
            isDragging.current = true;
            startY.current = e.touches[0].clientY;
            startScrollTop.current = content.scrollTop;

            // Keep visible
            showThumb();

            document.addEventListener("touchmove", handleTouchMove, { passive: false });
            document.addEventListener("touchend", handleTouchEnd);
        };

        const handleTouchMove = (e) => {
            if (!isDragging.current) return;

            showThumb();

            const deltaY = e.touches[0].clientY - startY.current;
            const scrollHeight = content.scrollHeight;
            const clientHeight = content.clientHeight;

            const thumbHeight =
                (clientHeight / scrollHeight) * clientHeight;

            const maxScrollTop = scrollHeight - clientHeight;

            const scrollDelta =
                (deltaY / (clientHeight - thumbHeight)) * maxScrollTop;

            content.scrollTop = Math.max(
                0,
                Math.min(maxScrollTop, startScrollTop.current + scrollDelta)
            );

            e.preventDefault();
        };

        const handleTouchEnd = () => {
            isDragging.current = false;
            fadeOut();
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", handleTouchEnd);
        };

        updateThumb();

        content.addEventListener("scroll", updateThumb);
        window.addEventListener("resize", updateThumb);
        thumb.addEventListener("mousedown", handleMouseDown);
        thumb.addEventListener("touchstart", handleTouchStart);

        return () => {
            content.removeEventListener("scroll", updateThumb);
            window.removeEventListener("resize", updateThumb);
            thumb.removeEventListener("mousedown", handleMouseDown);
            thumb.removeEventListener("touchstart", handleTouchStart);
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
    }, []);

    return (
        <div className="custom-scroll-wrapper" style={{ height }}>
            <div ref={contentRef} className="custom-scroll-content">
                {children}
            </div>

            <div className="custom-scrollbar">
                <div ref={thumbRef} className="custom-scroll-thumb" />
            </div>
        </div>
    );
});

export default CustomScroll;
